import { ContentState } from "@whitehaven/ui";
import type {
  AdminLeadDetail,
  AdminLeadGuestConversionDuplicateReview,
  AdminLeadStageHistoryCursor,
  LeadId,
} from "@whitehaven/contracts";
import type { AuthorizationSubject } from "@whitehaven/permissions";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  captureProtectedStateGeneration,
  isProtectedStateGenerationCurrent,
  registerProtectedStateCleaner,
} from "../../protected-state";
import {
  adminCrmAuthorizationKey,
  canAccessAdminCrm,
} from "./admin-crm-authorization";
import { AdminCrmViewNavigation } from "./AdminCrmViewNavigation";
import { AdminLeadAssignmentPanel } from "./AdminLeadAssignmentPanel";
import { AdminLeadAppointmentConversionPanel } from "./AdminLeadAppointmentConversionPanel";
import { AdminLeadDuplicatePanel } from "./AdminLeadDuplicatePanel";
import { AdminLeadEngagementPanel } from "./AdminLeadEngagementPanel";
import { AdminLeadGuestConversionPanel } from "./AdminLeadGuestConversionPanel";
import {
  canViewAdminLeadDetail,
  isAdminLeadDetailConsistent,
  isAdminLeadStageHistoryConsistent,
  isAdminLeadStageHistoryPageConsistent,
  mergeAdminLeadStageHistory,
} from "./admin-lead-detail-model";
import type { AdminLeadDetailGateway } from "./lead-detail-gateway";
import type { AdminLeadDuplicateGateway } from "./lead-duplicate-gateway";
import type {
  AdminLeadAssigneeDirectoryGateway,
  AdminLeadAssignmentMutationGateway,
} from "./lead-assignment-gateway";
import type { AdminLeadEngagementGateways } from "./lead-engagement-gateway";
import type { AdminLeadGuestConversionMutationGateway } from "./lead-guest-conversion-gateway";
import type { AdminLeadAppointmentConversionGateways } from "./lead-appointment-conversion-gateway";
import {
  ADMIN_LEAD_STAGE_HISTORY_PAGE_SIZE,
  type AdminLeadStageHistoryGateway,
} from "./lead-stage-history-gateway";
import {
  AdminLeadDetailView,
  type AdminLeadDetailNotice,
  type AdminLeadStageHistoryViewState,
} from "./AdminLeadDetailView";

interface AdminLeadDetailRouteProps {
  readonly subject: AuthorizationSubject | null;
  readonly leadId: LeadId | null;
  readonly gateway: AdminLeadDetailGateway | null;
  readonly duplicateGateway?: AdminLeadDuplicateGateway | null;
  readonly stageHistoryGateway?: AdminLeadStageHistoryGateway | null;
  readonly assigneeDirectoryGateway?: AdminLeadAssigneeDirectoryGateway | null;
  readonly assignmentMutationGateway?: AdminLeadAssignmentMutationGateway | null;
  readonly engagementGateways?: AdminLeadEngagementGateways | null;
  readonly guestConversionMutationGateway?: AdminLeadGuestConversionMutationGateway | null;
  readonly appointmentConversionGateways?: AdminLeadAppointmentConversionGateways | null;
}

type AdminLeadDetailGatewayState =
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | { readonly kind: "not-found" }
  | {
      readonly kind: "ready";
      readonly detail: AdminLeadDetail;
      readonly refreshing: boolean;
      readonly notice: AdminLeadDetailNotice;
    };

function failedStageHistoryState(
  current: AdminLeadStageHistoryViewState,
  mode: "initial" | "refresh" | "load-more",
): AdminLeadStageHistoryViewState {
  if (mode === "initial" || current.kind !== "ready") {
    return { kind: "error" };
  }

  return {
    ...current,
    refreshing: false,
    loadingMore: false,
    notice: mode === "refresh" ? "REFRESH_FAILED" : "LOAD_MORE_FAILED",
  };
}

function AdminLeadDetailFrame({ children }: { readonly children: ReactNode }) {
  return (
    <section
      className="portal-page crm-lead-detail-page portal-screen-only"
      aria-labelledby="portal-title"
      aria-describedby="crm-lead-detail-description"
    >
      <header className="portal-page__header">
        <div>
          <p className="portal-kicker">CRM workspace</p>
          <h1 id="portal-title" tabIndex={-1} data-main-focus data-route-focus>
            Lead details
          </h1>
          <p id="crm-lead-detail-description">
            Review one authorized lead and the dated CRM facts available today.
          </p>
        </div>
        <a className="crm-lead-detail__back" href="/admin/crm/leads">
          Back to leads
        </a>
      </header>
      <AdminCrmViewNavigation current="leads" />
      <div className="portal-page__content">{children}</div>
    </section>
  );
}

function RouteState({
  kind,
  title,
  children,
  actions,
  live,
}: {
  readonly kind: "loading" | "warning" | "error";
  readonly title: string;
  readonly children: ReactNode;
  readonly actions?: ReactNode;
  readonly live?: boolean;
}) {
  return (
    <ContentState
      kind={kind}
      title={title}
      actions={actions}
      live={live ?? kind === "error"}
    >
      {children}
    </ContentState>
  );
}

function AuthorizedAdminLeadDetail({
  leadId,
  gateway,
  stageHistoryGateway,
  duplicateGateway,
  assigneeDirectoryGateway,
  assignmentMutationGateway,
  engagementGateways,
  guestConversionMutationGateway,
  appointmentConversionGateways,
}: {
  readonly leadId: LeadId;
  readonly gateway: AdminLeadDetailGateway;
  readonly stageHistoryGateway: AdminLeadStageHistoryGateway | null;
  readonly duplicateGateway: AdminLeadDuplicateGateway | null;
  readonly assigneeDirectoryGateway: AdminLeadAssigneeDirectoryGateway | null;
  readonly assignmentMutationGateway: AdminLeadAssignmentMutationGateway | null;
  readonly engagementGateways: AdminLeadEngagementGateways | null;
  readonly guestConversionMutationGateway: AdminLeadGuestConversionMutationGateway | null;
  readonly appointmentConversionGateways: AdminLeadAppointmentConversionGateways | null;
}) {
  const [gatewayState, setGatewayState] = useState<AdminLeadDetailGatewayState>(
    { kind: "loading" },
  );
  const requestSequence = useRef(0);
  const activeRequest = useRef<AbortController | null>(null);
  const [stageHistoryState, setStageHistoryState] =
    useState<AdminLeadStageHistoryViewState>(
      stageHistoryGateway ? { kind: "loading" } : { kind: "unavailable" },
    );
  const historyRequestSequence = useRef(0);
  const activeHistoryRequest = useRef<AbortController | null>(null);
  const [detailRefreshEpoch, setDetailRefreshEpoch] = useState(0);
  const [assignmentPending, setAssignmentPending] = useState(false);
  const assignmentPendingRef = useRef(false);
  const [engagementPending, setEngagementPending] = useState(false);
  const engagementPendingRef = useRef(false);
  const [conversionPending, setConversionPending] = useState(false);
  const conversionPendingRef = useRef(false);
  const [appointmentConversionPending, setAppointmentConversionPending] =
    useState(false);
  const appointmentConversionPendingRef = useRef(false);
  const [duplicateReview, setDuplicateReview] =
    useState<AdminLeadGuestConversionDuplicateReview | null>(null);

  const handleAssignmentPendingChange = useCallback((next: boolean) => {
    assignmentPendingRef.current = next;
    if (next) {
      requestSequence.current += 1;
      activeRequest.current?.abort();
      activeRequest.current = null;
      setGatewayState((current) =>
        current.kind === "ready"
          ? { ...current, refreshing: false, notice: null }
          : current,
      );
    }
    setAssignmentPending(next);
  }, []);

  const handleEngagementPendingChange = useCallback((next: boolean) => {
    engagementPendingRef.current = next;
    if (next) {
      requestSequence.current += 1;
      activeRequest.current?.abort();
      activeRequest.current = null;
      setGatewayState((current) =>
        current.kind === "ready"
          ? { ...current, refreshing: false, notice: null }
          : current,
      );
    }
    setEngagementPending(next);
  }, []);

  const handleConversionPendingChange = useCallback((next: boolean) => {
    conversionPendingRef.current = next;
    if (next) {
      requestSequence.current += 1;
      activeRequest.current?.abort();
      activeRequest.current = null;
      setGatewayState((current) =>
        current.kind === "ready"
          ? { ...current, refreshing: false, notice: null }
          : current,
      );
    }
    setConversionPending(next);
  }, []);

  const handleAppointmentConversionPendingChange = useCallback(
    (next: boolean) => {
      appointmentConversionPendingRef.current = next;
      if (next) {
        requestSequence.current += 1;
        activeRequest.current?.abort();
        activeRequest.current = null;
        setGatewayState((current) =>
          current.kind === "ready"
            ? { ...current, refreshing: false, notice: null }
            : current,
        );
      }
      setAppointmentConversionPending(next);
    },
    [],
  );

  const requestStageHistory = useCallback(
    async (
      mode: "initial" | "refresh" | "load-more",
      detail: AdminLeadDetail,
      cursor: AdminLeadStageHistoryCursor | null,
    ) => {
      if (!stageHistoryGateway) {
        setStageHistoryState({ kind: "unavailable" });
        return;
      }

      const requestId = historyRequestSequence.current + 1;
      historyRequestSequence.current = requestId;
      activeHistoryRequest.current?.abort();

      const controller = new AbortController();
      activeHistoryRequest.current = controller;
      const protectedStateGeneration = captureProtectedStateGeneration();

      setStageHistoryState((current) => {
        if (mode === "initial" || current.kind !== "ready") {
          return { kind: "loading" };
        }

        if (!isAdminLeadStageHistoryConsistent(current.items, detail)) {
          return { kind: "loading" };
        }

        return {
          ...current,
          refreshing: mode === "refresh",
          loadingMore: mode === "load-more",
          notice: null,
        };
      });

      try {
        const page = await stageHistoryGateway(
          {
            leadId,
            cursor,
            limit: ADMIN_LEAD_STAGE_HISTORY_PAGE_SIZE,
          },
          { signal: controller.signal },
        );

        if (
          controller.signal.aborted ||
          historyRequestSequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          return;
        }

        if (!isAdminLeadStageHistoryPageConsistent(page, detail)) {
          setStageHistoryState((current) =>
            failedStageHistoryState(current, mode),
          );
          return;
        }

        setStageHistoryState((current) => {
          if (
            mode === "load-more" &&
            (cursor === null ||
              page.nextCursor === cursor ||
              (page.nextCursor !== null &&
                current.kind === "ready" &&
                current.consumedCursors.includes(page.nextCursor)))
          ) {
            return failedStageHistoryState(current, mode);
          }

          const items =
            mode === "load-more" && current.kind === "ready"
              ? mergeAdminLeadStageHistory(current.items, page.items)
              : page.items;

          if (
            items === null ||
            !isAdminLeadStageHistoryConsistent(items, detail)
          ) {
            return failedStageHistoryState(current, mode);
          }

          return {
            kind: "ready",
            items,
            nextCursor: page.nextCursor,
            consumedCursors:
              mode === "load-more" &&
              current.kind === "ready" &&
              cursor !== null
                ? [...current.consumedCursors, cursor]
                : [],
            refreshing: false,
            loadingMore: false,
            notice: null,
          };
        });
      } catch {
        if (
          controller.signal.aborted ||
          historyRequestSequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          return;
        }

        setStageHistoryState((current) => {
          return failedStageHistoryState(current, mode);
        });
      } finally {
        if (activeHistoryRequest.current === controller) {
          activeHistoryRequest.current = null;
        }
      }
    },
    [leadId, stageHistoryGateway],
  );

  const requestDetail = useCallback(
    async (mode: "initial" | "refresh") => {
      if (
        mode === "refresh" &&
        (assignmentPendingRef.current ||
          engagementPendingRef.current ||
          conversionPendingRef.current ||
          appointmentConversionPendingRef.current)
      ) {
        return;
      }

      const requestId = requestSequence.current + 1;
      requestSequence.current = requestId;
      activeRequest.current?.abort();

      const controller = new AbortController();
      activeRequest.current = controller;
      const protectedStateGeneration = captureProtectedStateGeneration();

      setGatewayState((current) => {
        if (mode === "initial" || current.kind !== "ready") {
          return { kind: "loading" };
        }

        return {
          ...current,
          refreshing: true,
          notice: null,
        };
      });

      if (mode === "initial") {
        historyRequestSequence.current += 1;
        activeHistoryRequest.current?.abort();
        activeHistoryRequest.current = null;
        setStageHistoryState(
          stageHistoryGateway ? { kind: "loading" } : { kind: "unavailable" },
        );
      }
      setDuplicateReview(null);

      try {
        const detail = await gateway({ leadId }, { signal: controller.signal });

        if (
          controller.signal.aborted ||
          requestSequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          return;
        }

        if (!detail) {
          historyRequestSequence.current += 1;
          activeHistoryRequest.current?.abort();
          activeHistoryRequest.current = null;
          setStageHistoryState(
            stageHistoryGateway ? { kind: "loading" } : { kind: "unavailable" },
          );
          setGatewayState({ kind: "not-found" });
          return;
        }

        if (
          !isAdminLeadDetailConsistent(detail) ||
          detail.summary.leadId !== leadId
        ) {
          historyRequestSequence.current += 1;
          activeHistoryRequest.current?.abort();
          activeHistoryRequest.current = null;
          setStageHistoryState(
            stageHistoryGateway ? { kind: "loading" } : { kind: "unavailable" },
          );
          setGatewayState({ kind: "error" });
          return;
        }

        setGatewayState({
          kind: "ready",
          detail,
          refreshing: false,
          notice: null,
        });
        setDetailRefreshEpoch((current) => current + 1);
        void requestStageHistory(
          mode === "initial" ? "initial" : "refresh",
          detail,
          null,
        );
      } catch {
        if (
          controller.signal.aborted ||
          requestSequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          return;
        }

        setGatewayState((current) => {
          if (mode === "initial" || current.kind !== "ready") {
            return { kind: "error" };
          }

          return {
            ...current,
            refreshing: false,
            notice: "REFRESH_FAILED",
          };
        });
      }
    },
    [gateway, leadId, requestStageHistory, stageHistoryGateway],
  );

  useEffect(() => {
    const unregisterCleaner = registerProtectedStateCleaner(
      "crm-admin-lead-detail",
      () => {
        requestSequence.current += 1;
        activeRequest.current?.abort();
        activeRequest.current = null;
        historyRequestSequence.current += 1;
        activeHistoryRequest.current?.abort();
        activeHistoryRequest.current = null;
        setGatewayState({ kind: "loading" });
        setStageHistoryState(
          stageHistoryGateway ? { kind: "loading" } : { kind: "unavailable" },
        );
        assignmentPendingRef.current = false;
        setAssignmentPending(false);
        engagementPendingRef.current = false;
        setEngagementPending(false);
        conversionPendingRef.current = false;
        setConversionPending(false);
        appointmentConversionPendingRef.current = false;
        setAppointmentConversionPending(false);
        setDuplicateReview(null);
      },
    );

    void requestDetail("initial");

    return () => {
      unregisterCleaner();
      requestSequence.current += 1;
      activeRequest.current?.abort();
      activeRequest.current = null;
      historyRequestSequence.current += 1;
      activeHistoryRequest.current?.abort();
      activeHistoryRequest.current = null;
    };
  }, [requestDetail, stageHistoryGateway]);

  if (gatewayState.kind === "loading") {
    return (
      <RouteState kind="loading" title="Loading authorized lead details.">
        <p>The secure detail view is being prepared.</p>
      </RouteState>
    );
  }

  if (gatewayState.kind === "error") {
    return (
      <RouteState
        kind="error"
        title="The lead details could not be loaded."
        actions={
          <button type="button" onClick={() => void requestDetail("initial")}>
            Try again
          </button>
        }
      >
        <p>
          No lead information is shown. Try the authorized read again or return
          to the lead list.
        </p>
      </RouteState>
    );
  }

  if (gatewayState.kind === "not-found") {
    return (
      <RouteState
        kind="warning"
        title="The lead details are unavailable."
        actions={<a href="/admin/crm/leads">Return to the lead list</a>}
        live
      >
        <p>
          No lead information is shown. The record may not exist or may be
          outside this account&apos;s authorized scope.
        </p>
      </RouteState>
    );
  }

  return (
    <AdminLeadDetailView
      detail={gatewayState.detail}
      refreshing={gatewayState.refreshing}
      notice={gatewayState.notice}
      stageHistory={stageHistoryState}
      duplicatePanel={
        <AdminLeadDuplicatePanel
          leadId={gatewayState.detail.summary.leadId}
          gateway={duplicateGateway}
          refreshEpoch={detailRefreshEpoch}
          disabled={
            gatewayState.refreshing ||
            assignmentPending ||
            engagementPending ||
            conversionPending ||
            appointmentConversionPending
          }
          onReviewStateChange={setDuplicateReview}
          onSummaryConfirmed={(duplicateSummary) => {
            setGatewayState((current) =>
              current.kind === "ready" &&
              current.detail.summary.leadId ===
                gatewayState.detail.summary.leadId
                ? {
                    ...current,
                    detail: {
                      ...current.detail,
                      summary: {
                        ...current.detail.summary,
                        duplicateSummary,
                      },
                    },
                  }
                : current,
            );
          }}
        />
      }
      assignmentPanel={
        <AdminLeadAssignmentPanel
          lead={gatewayState.detail.summary}
          directoryGateway={assigneeDirectoryGateway}
          mutationGateway={assignmentMutationGateway}
          detailRefreshing={gatewayState.refreshing}
          externalMutationPending={
            engagementPending ||
            conversionPending ||
            appointmentConversionPending
          }
          refreshEpoch={detailRefreshEpoch}
          onConfirmed={(summary) => {
            setGatewayState((current) =>
              current.kind === "ready" &&
              current.detail.summary.leadId === summary.leadId
                ? {
                    ...current,
                    detail: { ...current.detail, summary },
                  }
                : current,
            );
          }}
          onPendingChange={handleAssignmentPendingChange}
          onRefreshRequired={() => void requestDetail("refresh")}
        />
      }
      conversionPanel={
        <AdminLeadGuestConversionPanel
          detail={gatewayState.detail}
          duplicateReview={duplicateReview}
          mutationGateway={guestConversionMutationGateway}
          detailRefreshing={gatewayState.refreshing}
          externalMutationPending={
            assignmentPending ||
            engagementPending ||
            appointmentConversionPending
          }
          onConfirmed={(detail) => {
            setGatewayState((current) =>
              current.kind === "ready" &&
              current.detail.summary.leadId === detail.summary.leadId
                ? { ...current, detail }
                : current,
            );
            setDuplicateReview(null);
          }}
          onPendingChange={handleConversionPendingChange}
          onRefreshRequired={() => void requestDetail("refresh")}
        />
      }
      appointmentConversionPanel={
        <AdminLeadAppointmentConversionPanel
          detail={gatewayState.detail}
          gateways={appointmentConversionGateways}
          detailRefreshing={gatewayState.refreshing}
          externalMutationPending={
            assignmentPending || engagementPending || conversionPending
          }
          onPendingChange={handleAppointmentConversionPendingChange}
          onRefreshRequired={() => void requestDetail("refresh")}
        />
      }
      engagementPanel={
        <AdminLeadEngagementPanel
          leadId={gatewayState.detail.summary.leadId}
          leadRecordStatus={gatewayState.detail.summary.leadRecordStatus}
          gateways={engagementGateways}
          detailRefreshing={gatewayState.refreshing}
          externalMutationPending={
            assignmentPending ||
            conversionPending ||
            appointmentConversionPending
          }
          onPendingChange={handleEngagementPendingChange}
        />
      }
      refreshDisabled={
        assignmentPending ||
        engagementPending ||
        conversionPending ||
        appointmentConversionPending
      }
      onRefresh={() => void requestDetail("refresh")}
      onRetryStageHistory={() =>
        void requestStageHistory(
          stageHistoryState.kind === "ready" ? "refresh" : "initial",
          gatewayState.detail,
          null,
        )
      }
      onLoadMoreStageHistory={() => {
        if (
          stageHistoryState.kind === "ready" &&
          stageHistoryState.nextCursor
        ) {
          void requestStageHistory(
            "load-more",
            gatewayState.detail,
            stageHistoryState.nextCursor,
          );
        }
      }}
    />
  );
}

export function AdminLeadDetailRoute({
  subject,
  leadId,
  gateway,
  duplicateGateway = null,
  stageHistoryGateway = null,
  assigneeDirectoryGateway = null,
  assignmentMutationGateway = null,
  engagementGateways = null,
  guestConversionMutationGateway = null,
  appointmentConversionGateways = null,
}: AdminLeadDetailRouteProps) {
  if (!leadId) {
    return (
      <AdminLeadDetailFrame>
        <RouteState
          kind="warning"
          title="This CRM link cannot be opened."
          actions={<a href="/admin/crm/leads">Return to the lead list</a>}
        >
          <p>
            No lead information was requested. Use a valid link from the
            authorized lead list.
          </p>
        </RouteState>
      </AdminLeadDetailFrame>
    );
  }

  if (!subject) {
    return (
      <AdminLeadDetailFrame>
        <RouteState
          kind="warning"
          title="Secure sign-in is required."
          actions={<a href="/">Return to portal home</a>}
        >
          <p>
            Lead information stays unavailable until a verified portal session
            supplies an authorized account.
          </p>
        </RouteState>
      </AdminLeadDetailFrame>
    );
  }

  if (!canViewAdminLeadDetail(subject)) {
    return (
      <AdminLeadDetailFrame>
        <RouteState
          kind="warning"
          title="This account cannot open lead details."
          actions={<a href="/">Return to portal home</a>}
        >
          <p>
            No lead information was requested or displayed. Use an approved
            account with CRM access.
          </p>
        </RouteState>
      </AdminLeadDetailFrame>
    );
  }

  if (!gateway) {
    return (
      <AdminLeadDetailFrame>
        <RouteState
          kind="warning"
          title="The secure lead connection is not available yet."
          actions={<a href="/admin/crm/leads">Return to the lead list</a>}
        >
          <p>
            The permission gate and detail route are ready, but the authorized
            CRM read service has not been connected. No sample or cached lead is
            shown.
          </p>
        </RouteState>
      </AdminLeadDetailFrame>
    );
  }

  const canCreate = canAccessAdminCrm(subject, "create");
  const canWrite = canAccessAdminCrm(subject, "write");
  const authorizedEngagementGateways = engagementGateways
    ? {
        ...engagementGateways,
        mutateTag: canWrite ? engagementGateways.mutateTag : null,
        mutateTask: canWrite ? engagementGateways.mutateTask : null,
        mutateReminder: canWrite ? engagementGateways.mutateReminder : null,
        createNote: canCreate ? engagementGateways.createNote : null,
      }
    : null;

  return (
    <AdminLeadDetailFrame>
      <AuthorizedAdminLeadDetail
        key={`${adminCrmAuthorizationKey(subject)}:${leadId}`}
        leadId={leadId}
        gateway={gateway}
        duplicateGateway={duplicateGateway}
        stageHistoryGateway={stageHistoryGateway}
        assigneeDirectoryGateway={assigneeDirectoryGateway}
        assignmentMutationGateway={
          canWrite ? assignmentMutationGateway : null
        }
        engagementGateways={authorizedEngagementGateways}
        guestConversionMutationGateway={
          canCreate ? guestConversionMutationGateway : null
        }
        appointmentConversionGateways={
          canCreate ? appointmentConversionGateways : null
        }
      />
    </AdminLeadDetailFrame>
  );
}
